import { useState } from "react";
import { api } from "@/lib/api";
import { Search, ExternalLink, MapPin, BedDouble, Ruler, Car } from "lucide-react";

const brl = (v) => (v ? `R$ ${Number(v).toLocaleString("pt-BR")}` : "sob consulta");

export default function AgencyPropertySearch() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [neighborhood, setNeighborhood] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [bedrooms, setBedrooms] = useState("");
  const [totalScraped, setTotalScraped] = useState(0);
  const [firstRun, setFirstRun] = useState(true);

  const runSearch = async () => {
    setLoading(true);
    setFirstRun(false);
    const q = new URLSearchParams();
    if (neighborhood) q.set("neighborhood", neighborhood);
    if (maxPrice) q.set("max_price", maxPrice);
    if (bedrooms) q.set("bedrooms", bedrooms);
    try {
      const { data } = await api.get(`/agency/property-search?${q.toString()}`);
      setItems(data.items);
      setTotalScraped(data.total_scraped);
    } catch (e) {
      setItems([]);
      setTotalScraped(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="agency-property-search">
      <div>
        <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Pauta da imobiliária</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-extrabold mt-1">Buscador de imóveis</h1>
        <p className="text-white/60 text-sm mt-2 max-w-3xl">
          Pesquisa em tempo real no site oficial da sua imobiliária. Filtre por bairro, orçamento e dormitórios pra achar
          rápido o imóvel certo pro cliente.
        </p>
      </div>

      <div className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Bairro</label>
            <input
              data-testid="ps-neighborhood"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
              placeholder="Ex: Ingleses, Jurerê, Trindade..."
              className="w-full mt-1 px-3 py-2.5 bg-[#0A1937] border border-white/10 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#3B9EFF]/50"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Orçamento máximo</label>
            <input
              data-testid="ps-max-price"
              type="number"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="800000"
              className="w-full mt-1 px-3 py-2.5 bg-[#0A1937] border border-white/10 rounded-lg text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-[#3B9EFF]/50"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Dormitórios (mín.)</label>
            <select
              data-testid="ps-bedrooms"
              value={bedrooms}
              onChange={(e) => setBedrooms(e.target.value)}
              className="w-full mt-1 px-3 py-2.5 bg-[#0A1937] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B9EFF]/50"
            >
              <option value="">Qualquer</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-white/50">
            {totalScraped > 0 && `${items.length} de ${totalScraped} imóveis no site`}
          </div>
          <button
            data-testid="ps-run-btn"
            onClick={runSearch}
            disabled={loading}
            className="px-6 py-2.5 bg-[#3B9EFF] hover:bg-[#5AAEFF] text-[#071129] font-bold rounded-lg text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <Search className="w-4 h-4" />
            {loading ? "Buscando..." : "Buscar imóveis"}
          </button>
        </div>
      </div>

      {firstRun ? (
        <div className="p-12 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <Search className="w-10 h-10 mx-auto text-white/30 mb-3" />
          <div className="text-white/70 font-semibold">Comece a busca</div>
          <div className="text-white/50 text-xs mt-2">
            Preencha os filtros acima e clique em Buscar imóveis pra ver a pauta da imobiliária.
          </div>
        </div>
      ) : loading ? (
        <div className="p-8 text-white/60 text-center">Buscando no site da imobiliária...</div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center bg-[#0F1F3D] border border-dashed border-white/15 rounded-2xl">
          <div className="text-white/70">Nenhum imóvel encontrado com esses filtros.</div>
          <div className="text-white/50 text-xs mt-2">Tente ampliar o orçamento ou remover algum filtro.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <a
              key={i}
              href={it.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ps-card-${i}`}
              className="bg-[#0F1F3D] border border-white/10 rounded-2xl p-5 hover:border-[#3B9EFF]/50 transition group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="text-[10px] uppercase tracking-wider text-[#3B9EFF] font-bold">{it.type || "Imóvel"}</div>
                <ExternalLink className="w-4 h-4 text-white/40 group-hover:text-[#3B9EFF] transition" />
              </div>
              <div className="text-white font-heading font-bold text-lg leading-tight">{brl(it.price)}</div>
              <div className="text-white/60 text-sm mt-2 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                <span>
                  {it.neighborhood || "-"} · {it.city || "-"}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-4 text-white/60 text-xs">
                {it.bedrooms != null && (
                  <span className="flex items-center gap-1">
                    <BedDouble className="w-3.5 h-3.5" /> {it.bedrooms} dorms
                  </span>
                )}
                {it.parking != null && (
                  <span className="flex items-center gap-1">
                    <Car className="w-3.5 h-3.5" /> {it.parking} vagas
                  </span>
                )}
                {it.area_m2 != null && (
                  <span className="flex items-center gap-1">
                    <Ruler className="w-3.5 h-3.5" /> {it.area_m2} m²
                  </span>
                )}
              </div>
              {it.code && (
                <div className="mt-3 text-[10px] text-white/40 font-mono">cód {it.code}</div>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
